import { WonKeyPanel } from '@/components/giveaway/WonKeyPanel'
import { GiveawayEntrants } from '@/components/giveaway/GiveawayEntrants'
import { useMemo } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { useGiveaway, useGiveawayTypes, useMe } from '@/hooks/queries'
import { entryOptionLabel, isFirstEntries, referralSummary } from '@/models/giveaway'
import { useGiveawayEntry } from '@/hooks/use-giveaway-entry'
import { useCaptureReferral, useReferralLink } from '@/hooks/use-referral-link'
import { ReferralIcon } from '@/components/giveaway/ReferralIcon'
import { useOverlayStore } from '@/store/overlays'
import { useTicker } from '@/hooks/use-ticker'
import { useDocumentMeta } from '@/hooks/use-document-meta'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { giveawayPath, giveawayUrl } from '@/lib/slug'
import { ValueBadge, ValueBadgeSkeleton } from '@/components/giveaway/ValueBadge'
import { SkeletonLine } from '@/components/ui/Skeleton'
import { ShareButton } from '@/components/giveaway/ShareButton'
import { FeatureButton } from '@/components/giveaway/FeatureButton'
import { EntryHint } from '@/components/giveaway/EntryHint'
import { LinkedText } from '@/components/ui/LinkedText'
import { GameWebsiteLink } from '@/components/GameWebsiteLink'
import { MediaGallery } from '@/components/media/MediaGallery'
import { VideoEmbed } from '@/components/media/VideoEmbed'

/**
 * The public, shareable giveaway page at `/g/:slug` — a port of
 * pages/GiveawayDetails.vue.
 *
 * The Vue page fetched the giveaway and then rendered hard-coded placeholders
 * over the top of it: "$60", "9001" entries, "9 days left", three identical
 * "Join the Discord / +500" rows from `objectives: [1, 2, 3]`. It is bound to
 * the fetched record here, addressed by slug, and carries the share and edit
 * affordances the link is for.
 */
export function GiveawayDetailsPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: giveaway, isPending, isError } = useGiveaway(slug)

  const { data: me } = useMe()
  const editGiveaway = useOverlayStore((state) => state.editGiveaway)
  const { requestEntry, enteringObjectiveId } = useGiveawayEntry(giveaway)
  const copyReferralLink = useReferralLink(giveaway)
  useCaptureReferral(giveaway?.id)
  const location = useLocation()
  const { data: giveawayTypes } = useGiveawayTypes()
  const firstEntries = giveaway ? isFirstEntries(giveaway, giveawayTypes) : false

  useTicker(1000, !!giveaway && !giveaway.has_ended)

  const canonicalSlug = giveaway?.get_slug ?? ''

  useDocumentMeta(
    useMemo(
      () =>
        giveaway
          ? {
              title: giveaway.name ?? 'Giveaway',
              description:
                giveaway.description ??
                `Enter to win ${giveaway.game_info?.name ?? 'this giveaway'} on JustLoot.me.`,
              image: giveaway.display_cover.src,
              url: canonicalSlug ? giveawayUrl(canonicalSlug) : null,
            }
          : null,
      [giveaway, canonicalSlug],
    ),
  )

  if (isPending) return <GiveawayDetailsSkeleton />

  if (isError || !giveaway) {
    return (
      <div className="page-giveaway-details">
        <div className="empty-state">
          <div className="empty-state-title">Giveaway unavailable</div>
          <div className="empty-state-body">
            That giveaway could not be loaded. It may have ended or the link may have changed.
          </div>
        </div>
      </div>
    )
  }

  // Reached by id, or by a slug that has since been renamed: move to the
  // canonical address so the URL people copy from the bar is the shareable one.
  if (canonicalSlug && slug !== canonicalSlug) {
    // Keeps the query, which can carry a referral (?ref=).
    return <Navigate to={giveawayPath(canonicalSlug) + location.search} replace />
  }

  const remaining = giveaway.display_time_until_finish
  const isOwner = me?.id !== undefined && me.id !== null && me.id === giveaway.owner_id

  return (
    <div className="page-giveaway-details">
      <div className="giveaway-details rise">
        <div className="giveaway-content">
          <div className="giveaway-header">
            <div className="giveaway-header-names">
              {giveaway.owner?.username ? (
                <Link className="giveaway-username" to={`/u/${giveaway.owner.username}`}>
                  {giveaway.owner.display_name}
                </Link>
              ) : (
                <div className="giveaway-username">{giveaway.owner?.display_name}</div>
              )}
              <h1 className="giveaway-name">{giveaway.name}</h1>
            </div>
            <ValueBadge gameInfo={giveaway.game_info} className="is-large" />
          </div>

          <div
            className="giveaway-cover-media"
            style={backgroundImage(giveaway.display_cover.src)}
          />

          {giveaway.featured_video ? (
            <VideoEmbed
              url={giveaway.featured_video}
              title={`${giveaway.name ?? 'Giveaway'} video`}
            />
          ) : null}

          {giveaway.description ? (
            <div className="giveaway-description">
              <LinkedText text={giveaway.description} />
            </div>
          ) : null}

          <MediaGallery items={giveaway.display_gallery} title={giveaway.name ?? 'Giveaway'} />

          <GameWebsiteLink game={giveaway.game_info} />

          <div className="giveaway-actions">
            <ShareButton url={giveawayUrl(canonicalSlug)} />

            {isOwner ? (
              <button type="button" className="action-btn" onClick={() => editGiveaway(giveaway)}>
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  aria-hidden="true"
                >
                  <path d="M11.5 2.5 13.5 4.5 5 13H3v-2z" strokeLinejoin="round" />
                </svg>
                Edit
              </button>
            ) : null}

            {me?.is_admin ? <FeatureButton giveaway={giveaway} /> : null}
          </div>

          {giveaway.terms ? <div className="giveaway-terms">{giveaway.terms}</div> : null}
        </div>

        <div className="giveaway-entries-strip">
          {giveaway.shows_entry_count ? (
            <div
              className="giveaway-entries-total"
              title={giveaway.hide_entries ? 'Hidden from everyone but you' : undefined}
            >
              <div className="giveaway-entries-total-title">Total Entries</div>
              <div className="giveaway-entries-total-data">
                {giveaway.display_total_entries.toLocaleString()}
              </div>
            </div>
          ) : null}
          <div className="giveaway-entries-entered">
            <div className="giveaway-entries-entered-title">Your Entries</div>
            <div className="giveaway-entries-entered-data">
              {giveaway.display_my_entries.toLocaleString()}
            </div>
          </div>
          <div className="giveaway-entries-days">
            {/* Label over figure, like the two beside it. */}
            <div className="giveaway-entries-days-title">
              {giveaway.has_ended ? 'Status' : `${remaining.text} left`}
            </div>
            <div className="giveaway-entries-days-data">
              {giveaway.has_ended ? 'Ended' : remaining.digit}
            </div>
          </div>
        </div>

        <div className="giveaway-objectives">
          <WonKeyPanel giveaway={giveaway} />
          <EntryHint giveaway={giveaway} firstEntries={firstEntries} />

          {giveaway.giveaway_objective.map((objective) =>
            objective.is_referral ? (
              <button
                type="button"
                className="giveaway-objective is-referral"
                key={objective.key}
                disabled={giveaway.has_ended}
                onClick={() => void copyReferralLink(objective)}
              >
                <div className="giveaway-objective-section-logo">
                  <div className="giveaway-objective-logo">
                    <ReferralIcon size={26} />
                  </div>
                </div>
                <div className="giveaway-objective-section-name">
                  <div className="giveaway-objective-name">{objective.display_name}</div>
                  {!giveaway.has_ended ? (
                    <div className="giveaway-objective-sub">
                      {me
                        ? referralSummary(objective, firstEntries, giveaway.require_all_objectives)
                        : 'Sign in to get your referral link'}
                    </div>
                  ) : null}
                </div>
                <div className="giveaway-objective-section-points">
                  <div className="giveaway-objective-points">
                    {entryOptionLabel(objective, firstEntries, giveaway.require_all_objectives)}
                  </div>
                </div>
              </button>
            ) : (
              <button
                type="button"
                className={cn('giveaway-objective', objective.is_entered && 'entered')}
                key={objective.key}
                disabled={objective.is_entered || giveaway.has_ended}
                onClick={() => requestEntry(objective.id)}
              >
                <div className="giveaway-objective-section-logo">
                  <div className="giveaway-objective-logo">
                    <img src="/logos_discord.png" alt="" />
                  </div>
                </div>
                <div className="giveaway-objective-section-name">
                  <div className="giveaway-objective-name">{objective.display_name}</div>
                </div>
                <div className="giveaway-objective-section-points">
                  {objective.is_entered ? (
                    <div className="giveaway-objective-done">Entered ✓</div>
                  ) : enteringObjectiveId === objective.id ? (
                    <div className="giveaway-objective-points">
                      <span className="spinner-inline" aria-hidden="true" />
                    </div>
                  ) : (
                    <div className="giveaway-objective-points">
                      {entryOptionLabel(objective, firstEntries, giveaway.require_all_objectives)}
                    </div>
                  )}
                </div>
              </button>
            ),
          )}

          {giveaway.giveaway_objective.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-title">No entry options yet</div>
              <div className="empty-state-body">
                The organiser has not added a way to enter this giveaway.
              </div>
            </div>
          ) : null}
        </div>

        {isOwner ? <GiveawayEntrants giveaway={giveaway} /> : null}
      </div>
    </div>
  )
}

/**
 * The page's own markup with the data greyed out: owner and title, cover,
 * description, share button, the entries strip (real labels) and two entry
 * options.
 */
export function GiveawayDetailsSkeleton() {
  return (
    <div className="page-giveaway-details is-skeleton" aria-hidden="true">
      <div className="giveaway-details">
        <div className="giveaway-content">
          <div className="giveaway-header">
            <div className="giveaway-header-names">
              <div className="giveaway-username">
                <SkeletonLine width="7em" />
              </div>
              <div className="giveaway-name">
                <SkeletonLine width="82%" />
              </div>
            </div>
            <ValueBadgeSkeleton className="is-large" />
          </div>

          <div className="giveaway-cover-media skeleton" />

          <div className="giveaway-description">
            <SkeletonLine />
            <SkeletonLine />
            <SkeletonLine width="56%" />
          </div>

          <div className="giveaway-actions">
            {/* The real button's icon and label, invisible, so it is the same size. */}
            <span className="action-btn skeleton skeleton-ghost">
              <svg viewBox="0 0 16 16" aria-hidden="true" />
              Copy link
            </span>
          </div>
        </div>

        <div className="giveaway-entries-strip">
          <div className="giveaway-entries-total">
            <div className="giveaway-entries-total-title">Total Entries</div>
            <div className="giveaway-entries-total-data">
              <SkeletonLine width="2.4em" />
            </div>
          </div>
          <div className="giveaway-entries-entered">
            <div className="giveaway-entries-entered-title">Your Entries</div>
            <div className="giveaway-entries-entered-data">
              <SkeletonLine width="2.4em" />
            </div>
          </div>
          <div className="giveaway-entries-days">
            <div className="giveaway-entries-days-title">
              <SkeletonLine width="5em" />
            </div>
            <div className="giveaway-entries-days-data">
              <SkeletonLine width="1.4em" />
            </div>
          </div>
        </div>

        <div className="giveaway-objectives">
          {[0, 1].map((index) => (
            <div className="giveaway-objective" key={index}>
              <div className="giveaway-objective-section-logo">
                <div className="giveaway-objective-logo">
                  <div className="skeleton" style={{ width: 26, height: 26, borderRadius: 6 }} />
                </div>
              </div>
              <div className="giveaway-objective-section-name">
                <div className="giveaway-objective-name">
                  <SkeletonLine width="70%" />
                </div>
              </div>
              <div className="giveaway-objective-section-points">
                <div className="giveaway-objective-points">
                  <SkeletonLine width="1.6em" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
